import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { COMMAND_NAME_VALID_REGEXP, COMMAND_NAME_VALID_REGEXP_NOT_DOT } from 'src/app/conf/commands';
import { buildRbeIoStateAliasPatch, pickRbeIoStateRaw } from 'src/app/conf/rbe-io-state-label';
import { AutomationsAlias, InputsAlias, OutputsAlias } from 'src/app/model/automations-alias';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import {
	selectAutomationAlias,
	selectAutomationAliasEdited,
	selectAutomationAliasEditing
} from 'src/app/store/device/device.selector';

@Component({
	selector: 'app-edit-rbe-io-state-dialog',
	templateUrl: './edit-rbe-io-state.dialog.component.html',
})
export class EditRbeIoStateDialogComponent implements OnInit, OnDestroy {
	automationsAlias$: Observable<AutomationsAlias | undefined>;
	automationsAliasSub: Subscription | undefined;
	automationsAliasEdited$: Observable<boolean | undefined>;
	automationsAliasEditedSub: Subscription | undefined;
	automationAliasEditing$: Observable<boolean | undefined>;
	automationAliasEditingSub: Subscription | undefined;

	aliasForm: FormGroup = null;
	typeAlias: string;
	ioId: number;
	automationsAlias: AutomationsAlias;
	currentItem: InputsAlias | OutputsAlias | undefined;
	automationAliasEditing = false;
	screenWidth: number;

	TYPE_ALIAS_INPUT = 'TYPE_ALIAS_INPUT';
	TYPE_ALIAS_OUTPUT = 'TYPE_ALIAS_OUTPUT';

	constructor(
		private store: Store<AppState>,
		public ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		if (config.data !== undefined) {
			this.typeAlias = config.data.typeAlias;
			this.ioId = config.data.ioId;
		}
		this.automationsAlias$ = this.store.select(selectAutomationAlias);
		this.automationsAliasEdited$ = this.store.select(selectAutomationAliasEdited);
		this.automationAliasEditing$ = this.store.select(selectAutomationAliasEditing);
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getAutomationsAlias());
		this.automationsAliasSub = this.automationsAlias$.subscribe(automationsAlias => {
			this.automationsAlias = automationsAlias;
			this.buildForm();
		});
		this.automationsAliasEditedSub = this.automationsAliasEdited$.subscribe(succ => {
			if (succ) {
				this.store.dispatch(DeviceActions.getAutomationsAlias());
				this.ref.close();
			}
		});
		this.automationAliasEditingSub = this.automationAliasEditing$.subscribe(editing => {
			this.automationAliasEditing = !!editing;
		});
		StandardNewValidator.setFirstCompile(true);
		this.onResize();
	}

	buildForm(): void {
		const list = this.typeAlias === this.TYPE_ALIAS_INPUT
			? this.automationsAlias?.Inputs
			: this.automationsAlias?.Outputs;
		if (!list) {
			return;
		}
		this.currentItem = list.find(item => item.Id === this.ioId);
		const stateOn = this.translateData(pickRbeIoStateRaw(true, this.currentItem));
		const stateOff = this.translateData(pickRbeIoStateRaw(false, this.currentItem));
		const validators = [
			Validators.required,
			Validators.pattern(COMMAND_NAME_VALID_REGEXP),
			Validators.pattern(COMMAND_NAME_VALID_REGEXP_NOT_DOT)
		];
		this.aliasForm = new FormGroup({
			stateOn: new FormControl(stateOn, validators),
			stateOff: new FormControl(stateOff, validators)
		});
	}

	restoreDefault(field: 'stateOn' | 'stateOff'): void {
		if (!this.currentItem) {
			return;
		}
		const raw = field === 'stateOn'
			? this.currentItem.DefaultStateOn
			: this.currentItem.DefaultStateOff;
		this.aliasForm.controls[field].setValue(this.translateData(pickRbeIoStateRaw(field === 'stateOn', {
			StateOn: field === 'stateOn' ? raw : undefined,
			StateOff: field === 'stateOff' ? raw : undefined,
			DefaultStateOn: field === 'stateOn' ? raw : undefined,
			DefaultStateOff: field === 'stateOff' ? raw : undefined
		})));
	}

	ngOnDestroy(): void {
		this.automationsAliasSub?.unsubscribe();
		this.automationsAliasEditedSub?.unsubscribe();
		this.automationAliasEditingSub?.unsubscribe();
	}

	get f(): any {
		return this.aliasForm?.controls;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.aliasForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.aliasForm.get(key).errors;
			if (controlErrors != null) {
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (this.automationAliasEditing) {
			return;
		}
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const kind = this.typeAlias === this.TYPE_ALIAS_INPUT ? 'input' : 'output';
		const items = kind === 'input' ? this.automationsAlias.Inputs : this.automationsAlias.Outputs;
		const automationsAlias = buildRbeIoStateAliasPatch(
			kind,
			items,
			this.ioId,
			this.f.stateOn.value,
			this.f.stateOff.value,
			(s: string) => this.translateData(s)
		);
		this.store.dispatch(DeviceActions.editAutomationsAlias({ automationsAlias }));
	}

	closeDialog(): void {
		this.ref.close();
	}

	translateData(str: string): string {
		if (!str) {
			return '';
		}
		let resStr = '';
		const fields = str.split('{');
		for (const field of fields) {
			if (field.indexOf('}') >= 0) {
				const toTranslate = field.split('}');
				resStr = resStr + this.translate.instant(toTranslate[0]) + toTranslate[1];
			} else {
				resStr = resStr + field;
			}
		}
		return resStr;
	}

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-18rem');
	}

	newShowErrors(type: EditRbeIoStateDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: EditRbeIoStateDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
